import { type DataThemeOptions } from '@saviynt/design-system';

// URL's
export const BASENAME = '/ECMv6';

// Savi Copilot
export const SAVI_BASENAME = '/savi';
export const SAVI_ASSISTANT_BASENAME = `${SAVI_BASENAME}/assistant`;
export const SAVI_ASSISTANT_CHAT = `${SAVI_ASSISTANT_BASENAME}/new-chat`;

export const SAVI_ASSISTANT_ALLOWED_URLS = [
  SAVI_ASSISTANT_BASENAME,
  SAVI_ASSISTANT_CHAT,
];
export const SAVI_ASSISTANT_HEADER_URLS = [
  SAVI_ASSISTANT_BASENAME,
  SAVI_ASSISTANT_CHAT,
];

// Service Identities
export const SERVICE_IDENTITY_BASENAME = `/agent`;
export const SERVICE_IDENTITY_LIST = `${SERVICE_IDENTITY_BASENAME}/nhi/service-identity`;
export const SERVICE_IDENTITY_LIST_CREATE = `${SERVICE_IDENTITY_LIST}/create`;

export const SERVICE_IDENTITY_ALLOWED_URLS = [
  SERVICE_IDENTITY_LIST,
  SERVICE_IDENTITY_LIST_CREATE,
];
export const SERVICE_IDENTITY_HEADER_URLS = [SERVICE_IDENTITY_LIST];

export const COPILOT_WORKFLOWS: Record<
  string,
  {
    key: string;
    chatTitle: string;
    textContent: string;
    inputPlaceholder: string;
    prompt?: string;
  }
> = {
  CT_LAUNCH: {
    key: 'certification-launch-campaign',
    chatTitle: 'Launch Campaign',
    textContent:
      'Upload a policy document or let Savi know what you want to certify.',
    inputPlaceholder: 'How can I help you with launching campaign?',
    prompt: 'I want to launch a campaign',
  },
  RQ_APPROVE: {
    key: 'approve-user-request',
    chatTitle: 'Approve Request',
    textContent:
      'Search for a request by name or ID and let Savi know if you want to approve or reject it.',
    inputPlaceholder: 'How can I help you with approving request?',
    prompt: 'I want to approve a request',
  },
  RQ_SA_CREATE: {
    key: 'create-new-service-account',
    chatTitle: 'Create new Service Account',
    textContent:
      'Submit a Service Account request for applications, assign owners and access.',
    inputPlaceholder:
      'What can I help you with for Service Account Access Request?',
    prompt: 'I want to request a new Service Account',
  },
  RQ_SA_MODIFY: {
    key: 'modify-existing-service-account',
    chatTitle: 'Modify existing Service Account',
    textContent: 'I want to modify an existing Service Account',
    inputPlaceholder: 'How can I help you to modify service account?',
    prompt: 'I want to modify an existing Service Account',
  },
  RQ_SA_SHOW: {
    key: 'show-service-account',
    chatTitle: 'Show me my Service Account',
    textContent: 'I want to see my existing Service Accounts',
    inputPlaceholder: 'How can I help you with service account?',
    prompt: 'I want to see my existing Service Accounts',
  },
  RQ_SA_DISABLE: {
    key: 'disable-existing-service-account',
    chatTitle: 'Disable existing Service Account',
    textContent: 'I want to disable an existing Service Account',
    inputPlaceholder: 'How can I help you to disable service account?',
    prompt: 'I want to disable an existing Service Account',
  },
  RQ_SA_ENABLE: {
    key: 'enable-existing-service-account',
    chatTitle: 'Enable existing Service Account',
    textContent: 'I want to enable an existing Service Account',
    inputPlaceholder: 'How can I help you to enable service account?',
    prompt: 'I want to enable an existing Service Account',
  },
  RQ_SA_DELETE: {
    key: 'delete-existing-service-account',
    chatTitle: 'Delete Service Account',
    textContent: 'I want to delete an existing Service Account',
    inputPlaceholder: 'How can I help you to delete service account?',
    prompt: 'I want to delete an existing Service Account',
  },
  RQ_SA_LOCK: {
    key: 'lock-existing-service-account',
    chatTitle: 'Lock existing Service Account',
    textContent: 'I want to lock an existing Service Account',
    inputPlaceholder: 'How can I help you to lock service account?',
    prompt: 'I want to lock an existing Service Account',
  },
  RQ_SA_UNLOCK: {
    key: 'unlock-existing-service-account',
    chatTitle: 'Unlock existing Service Account',
    textContent: 'I want to unlock an existing Service Account',
    inputPlaceholder: 'How can I help you to unlock service account?',
    prompt: 'I want to unlock an existing Service Account',
  },
  RQ_AC_CREATE: {
    key: 'create-new-application-request',
    chatTitle: 'Create new Application Request',
    textContent:
      'Submit an application request and let Savi know what you want to update.',
    inputPlaceholder: 'What can I help you with for Application Request?',
    prompt: 'I want to create a new Application Request',
  },
  RQ_AC_MODIFY: {
    key: 'modify-existing-application-request',
    chatTitle: 'Modify existing Application Request',
    textContent: 'I want to modify an existing Application Request',
    inputPlaceholder: 'What can I help you with for Application Request?',
    prompt: 'I want to modify an existing Application Request',
  },
  RQ_AC_ENABLE: {
    key: 'enable-existing-application-request',
    chatTitle: 'Enable existing Application Request',
    textContent: 'I want to enable an existing Application Request',
    inputPlaceholder: 'What can I help you with for Application Request?',
    prompt: 'I want to enable an existing Application Request',
  },
  RQ_AC_DISABLE: {
    key: 'disable-existing-application-request',
    chatTitle: 'Disable existing Application Request',
    textContent: 'I want to disable an existing Application Request',
    inputPlaceholder: 'What can I help you with for Application Request?',
    prompt: 'I want to disable an existing Application Request',
  },
  RQ_AC_REMOVE: {
    key: 'remove-existing-application-request',
    chatTitle: 'Remove existing Application Request',
    textContent: 'I want to remove an existing Application Request',
    inputPlaceholder: 'What can I help you with for Application Request?',
    prompt: 'I want to remove an existing Application Request',
  },
  DEFAULT: {
    key: 'default',
    chatTitle: 'Savi Copilot',
    textContent: '',
    inputPlaceholder: 'What can I help you with?',
  },
};

export const APPLICATION_WORKFLOW_KEYS = [
  COPILOT_WORKFLOWS.RQ_AC_CREATE.key,
  COPILOT_WORKFLOWS.RQ_AC_MODIFY.key,
  COPILOT_WORKFLOWS.RQ_AC_ENABLE.key,
  COPILOT_WORKFLOWS.RQ_AC_DISABLE.key,
  COPILOT_WORKFLOWS.RQ_AC_REMOVE.key,
];

export const PROMPTS = {
  SA_RQ: {
    key: 'service-account',
    prompts: [
      COPILOT_WORKFLOWS.RQ_SA_CREATE,
      COPILOT_WORKFLOWS.RQ_SA_MODIFY,
      COPILOT_WORKFLOWS.RQ_SA_SHOW,
    ],
  },
  AC_RQ: {
    key: 'application-request',
    prompts: [
      COPILOT_WORKFLOWS.RQ_AC_CREATE,
      COPILOT_WORKFLOWS.RQ_AC_MODIFY,
      COPILOT_WORKFLOWS.RQ_AC_ENABLE,
    ],
  },
  DEFAULT: {
    key: 'default',
    prompts: [
      COPILOT_WORKFLOWS.RQ_SA_CREATE,
      COPILOT_WORKFLOWS.RQ_SA_MODIFY,
      COPILOT_WORKFLOWS.RQ_SA_SHOW,
    ],
  },
};

// Supported Themes
export const SYSTEM_DATA_THEMES: Record<'LIGHT' | 'DARK', DataThemeOptions> = {
  LIGHT: 'light',
  DARK: 'dark',
};

// Shorthand for the color tokens to reduce code clutter
export const CF_PRI_MED = '--color-foreground-primary-medium';
export const CF_SEC_BLD = '--color-foreground-secondary-bold';
export const CF_SEC_MED = '--color-foreground-secondary-medium';
export const CF_NEU_BLD = '--color-foreground-neutral-bold';
export const CF_NEU_DIS = '--color-foreground-neutral-disabled';
export const CF_NEU_MED = '--color-foreground-neutral-medium';
export const CF_NEU_STL = '--color-foreground-neutral-subtle';
export const CF_NEU_INV = '--color-foreground-neutral-inverse';
export const CF_CRT_BLD = '--color-foreground-critical-bold';
export const CF_CRT_MED = '--color-foreground-critical-medium';
export const CF_WRN_BLD = '--color-foreground-warning-bold';
export const CF_WRN_STL = '--color-foreground-warning-subtle';
export const CF_INF_MED = '--color-foreground-info-medium';
export const CF_INF_STL = '--color-foreground-info-subtle';
export const CF_SAV_MED = '--color-foreground-savi-medium';
export const CF_SAV_BLD = '--color-foreground-savi-bold';
export const CF_BLU_BLD = '--color-foreground-blue-bold';
export const CF_SUS_BLD = '--color-foreground-success-bold';
export const CF_SUS_STL = '--color-foreground-success-subtle';

// Media Queries
/**
 * Provides a guide for UX design breakpoints and their corresponding naming structure.
 * This is a reference for responsive design, indicating the pixel width and associated
 * designations for various device sizes.
 *
 * Mobile:
 * - 360px: 140 MobileXSmall (This is below our minimum, but it’s a common Android size, adding it here just in case)
 * - 375px: 130 MobileSmall
 * - 480px: 120 MobileLarge
 * - 600px: 110 MobileXLarge
 *
 * Tablet:
 * - 768px: 100 TabletXSmall
 * - 834px: 90 TabletSmall
 * - 900px: 80 TabletMedium
 * - 960px: 70 TabletLarge
 * - 1024px: 60 TabletXLarge
 *
 * Desktop:
 * - 1280px: 50 DesktopSmall
 * - 1366px: 40 DesktopMedium
 * - 1440px: 30 DesktopLarge
 * - 1600px: 20 DesktopXLarge
 * - 1920px: 10 DesktopXXLarge
 */
export const SCREEN_SIZE_MOBILE_LARGE = 'MobileLarge';
export const SCREEN_SIZE_MOBILE_XLARGE = 'MobileXLarge';
export const SCREEN_SIZE_TABLET_XSMALL = 'TabletXSmall';
export const SCREEN_SIZE_TABLET_SMALL = 'TabletSmall';
export const SCREEN_SIZE_TABLET_MEDIUM = 'TabletMedium';
export const SCREEN_SIZE_TABLET_LARGE = 'TabletLarge';
export const SCREEN_SIZE_DESKTOP_SMALL = 'DesktopSmall';

export const SCREEN_WIDTH_QUERIES = [
  { query: '(max-width: 479px)', value: 120, label: SCREEN_SIZE_MOBILE_LARGE },
  { query: '(max-width: 599px)', value: 110, label: SCREEN_SIZE_MOBILE_XLARGE },
  { query: '(max-width: 767px)', value: 100, label: SCREEN_SIZE_TABLET_XSMALL },
  { query: '(max-width: 833px)', value: 90, label: SCREEN_SIZE_TABLET_SMALL },
  { query: '(max-width: 899px)', value: 80, label: SCREEN_SIZE_TABLET_MEDIUM },
  { query: '(max-width: 959px)', value: 80, label: SCREEN_SIZE_TABLET_LARGE },
  { query: '(max-width: 1279px)', value: 50, label: SCREEN_SIZE_DESKTOP_SMALL },
];

// Alert Banner
export const GLOBAL_ALERT_BANNER_TYPES = {
  CRITICAL: 'Critical',
  WARNING: 'Warning',
  SUCCESS: 'Success',
  INFO: 'Information',
};

export const ALERT_BANNER_DEFAULT = {
  kind: GLOBAL_ALERT_BANNER_TYPES.INFO,
  title: '',
  description: '',
  link: undefined,
  isOpen: false,
  dataTheme: SYSTEM_DATA_THEMES.LIGHT,
};

export const MIN_COMMENT_LENGTH = 3;
export const DEFAULT_SEARCH_DEBOUNCE = 500;

// Chat
export const CHAT_MODE = {
  USER: 'prompt',
  ASSISTANT: 'response',
  ACTION: 'generating',
};

export const CHAT_BLOCK = {
  PROGRESS: 'progress',
  ERROR: 'error',
  MARKDOWN: 'markdown',
  SELECTABLES: 'selectables',
  ACTIONS: 'actions',
  TABLE: 'table',
  FORM: 'form',
  FORMFORGE: 'form-forge',
  COMBINEDFORM: 'combined-form',
  CUSTOM: 'custom',
  REVIEW: 'review',
  SOD: 'sod',
};

export const LIMIT_INLINE_COUNT = 3;
export const IS_ELIGIBLE = false;
